import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { RouteData, POI } from '@cya-routes/shared'
import { getPOITypeById, getPOICategoryById } from '../../data/poiTypes'
import { ChevronDown, ChevronUp } from 'lucide-react'


interface ElevationProfileProps {
  route: RouteData
  onClose: () => void
  className?: string
  pois?: POI[]
  isMasterRoute?: boolean
  segmentRoutes?: RouteData[]
  onHoverDistanceChange?: (distance: number | null) => void
  isMinimized?: boolean
  onToggleMinimize?: () => void
  scaleFactor?: number
  onClimbHover?: (climbInfo: { index: number, climb: any } | null) => void
  flybyProgress?: number // 0-100 percentage of flyby progress
}

// Function to interpolate between colors based on gradient value
const interpolateColor = (gradient: number): string => {
  const colors = [
    { threshold: 0, color: [134, 239, 172] },   // #86efac Green 300 <2%
    { threshold: 2, color: [254, 240, 138] },   // #fef08a Yellow 200 2-5%
    { threshold: 5, color: [255, 214, 165] },   // #ffd6a5 Orange 5-8%
    { threshold: 8, color: [254, 202, 202] },   // #fecaca Red 200 8-11%
    { threshold: 11, color: [252, 165, 165] }   // #fca5a5 Red 300 11%+
  ]
  
  // Clamp gradient to reasonable range
  const clampedGradient = Math.max(0, Math.min(20, gradient))
  
  // Find the two colors to interpolate between
  let lowerColor = colors[0]
  let upperColor = colors[colors.length - 1]
  
  for (let i = 0; i < colors.length - 1; i++) {
    if (clampedGradient >= colors[i].threshold && clampedGradient <= colors[i + 1].threshold) {
      lowerColor = colors[i]
      upperColor = colors[i + 1]
      break
    }
  }
  
  // Calculate interpolation factor
  const range = upperColor.threshold - lowerColor.threshold
  const factor = range > 0 ? (clampedGradient - lowerColor.threshold) / range : 0
  
  // Interpolate RGB values
  const r = Math.round(lowerColor.color[0] + (upperColor.color[0] - lowerColor.color[0]) * factor)
  const g = Math.round(lowerColor.color[1] + (upperColor.color[1] - lowerColor.color[1]) * factor)
  const b = Math.round(lowerColor.color[2] + (upperColor.color[2] - lowerColor.color[2]) * factor)
  
  return `rgb(${r}, ${g}, ${b})`
}

export const ElevationProfile = React.memo(function ElevationProfile({ route, className = '', pois = [], isMasterRoute = false, segmentRoutes = [], onHoverDistanceChange, isMinimized = false, onToggleMinimize, scaleFactor = 1, onClimbHover, flybyProgress }: ElevationProfileProps) {
  const elevationData = route.elevation?.profile || []
  const surfaces = route.surfaces || []
  
  // Runtime climb detection for routes that don't have climb data
  const [runtimeClimbs, setRuntimeClimbs] = useState<any[]>([])
  const [hoveredClimbIndex, setHoveredClimbIndex] = useState<number | null>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  
  // Use either stored climbs or runtime detected climbs
  const climbs = route.elevation?.climbs || runtimeClimbs
  
  // Debounced hover handlers
  const handleClimbMouseEnter = useCallback((index: number) => {
    // Clear any pending timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    // Set hover immediately
    setHoveredClimbIndex(index)
    
    // Call the parent callback with climb info
    if (onClimbHover && climbs && climbs[index]) {
      onClimbHover({ index, climb: climbs[index] })
    }
  }, [onClimbHover, climbs])
  
  const handleClimbMouseLeave = useCallback(() => {
    // Add a longer delay before removing hover to prevent flicker
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredClimbIndex(null)
      // Clear parent hover state
      if (onClimbHover) {
        onClimbHover(null)
      }
    }, 200)
  }, [onClimbHover])
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])
  
  // Detect climbs using fresh DEM data if not already present in route data
  useEffect(() => {
    console.log('🔍 Climb detection check:', {
      hasStoredClimbs: !!(route.elevation?.climbs && route.elevation.climbs.length > 0),
      hasCoordinates: !!(route.coordinates && route.coordinates.length > 0),
      hasElevationProfile: elevationData.length > 0,
      elevationProfilePoints: elevationData.length
    })
    
    if (route.elevation?.climbs && route.elevation.climbs.length > 0) {
      // Use existing climb data
      console.log('✅ Using stored climbs:', route.elevation.climbs.length)
      setRuntimeClimbs([])
      return
    }
    
    // For now, just detect from existing elevation profile since DEM requires map
    if (elevationData.length > 0) {
      const detectClimbsFromProfile = async () => {
        try {
          console.log('🏔️ Detecting climbs from elevation profile...')
          
          // Check elevation range
          const elevations = elevationData.map(p => p.elevation)
          const minElev = Math.min(...elevations)
          const maxElev = Math.max(...elevations)
          const totalGain = elevationData.reduce((sum, p, i) => {
            if (i === 0) return 0
            const gain = p.elevation - elevationData[i-1].elevation
            return sum + (gain > 0 ? gain : 0)
          }, 0)
          
          console.log('📊 Elevation stats:', {
            points: elevationData.length,
            minElevation: minElev.toFixed(0) + 'm',
            maxElevation: maxElev.toFixed(0) + 'm',
            range: (maxElev - minElev).toFixed(0) + 'm',
            totalGain: totalGain.toFixed(0) + 'm',
            avgPointSpacing: ((elevationData[elevationData.length-1]?.distance || 0) * 1000 / elevationData.length).toFixed(0) + 'm'
          })
          
          const { ClimbDetectionService } = await import('../../services/climbDetectionService')
          const detectedClimbs = ClimbDetectionService.detectClimbs(elevationData)
          console.log('✅ Detected climbs:', detectedClimbs.length)
          setRuntimeClimbs(detectedClimbs)
        } catch (error) {
          console.error('Failed to detect climbs:', error)
        }
      }
      
      detectClimbsFromProfile()
    } else {
      console.log('⚠️ No elevation profile data available for climb detection')
    }
  }, [route.elevation?.climbs, route.coordinates, elevationData])
  
  // Debug logging
  useEffect(() => {
    console.log('🏔️ Elevation Profile Debug:', {
      routeId: route.id,
      hasStoredClimbs: !!(route.elevation?.climbs),
      storedClimbsCount: route.elevation?.climbs?.length || 0,
      runtimeClimbsCount: runtimeClimbs.length,
      totalClimbs: climbs.length,
      elevationProfileLength: elevationData.length,
      hasCoordinates: !!(route.coordinates && route.coordinates.length > 0)
    })
    
    // Log climb details
    if (climbs && climbs.length > 0) {
      console.log('🚩 Climb details:', climbs.map(c => ({
        category: c.category,
        startKm: c.startDistance.toFixed(2),
        endKm: c.endDistance.toFixed(2),
        gain: c.elevationGain.toFixed(0) + 'm',
        grade: c.averageGrade.toFixed(1) + '%',
        color: c.color
      })))
    }
  }, [route.id, route.elevation?.climbs, runtimeClimbs, climbs, elevationData.length, route.coordinates])
  
  // Responsive margins based on screen size
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth
      setIsMobile(width < 590) // Mobile phones only
      setIsTablet(width >= 590 && width < 900) // Tablets in portrait
    }
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])
  
  const { maxElevation, minElevation, totalDistance } = useMemo(() => {
    if (elevationData.length === 0) {
      return { maxElevation: 0, minElevation: 0, totalDistance: 0 }
    }

    const elevations = elevationData.map(point => point.elevation)
    const max = Math.max(...elevations)
    const min = Math.min(...elevations)
    const distance = elevationData[elevationData.length - 1]?.distance || 0

    // Ensure a minimum elevation range of 100m for better visibility
    const range = max - min
    const MIN_ELEVATION_RANGE = 100
    
    if (range < MIN_ELEVATION_RANGE) {
      const neededRange = MIN_ELEVATION_RANGE - range
      
      // Never go below 0 on the elevation axis
      let adjustedMin = min
      let adjustedMax = max
      
      if (min - neededRange / 2 < 0) {
        // If going below 0, set min to 0 and add all padding to the top
        adjustedMin = Math.max(0, min - 10) // Small padding from 0 if possible
        adjustedMax = adjustedMin + MIN_ELEVATION_RANGE
      } else {
        // Otherwise distribute padding equally
        adjustedMin = min - neededRange / 2
        adjustedMax = max + neededRange / 2
      }
      
      return {
        maxElevation: adjustedMax,
        minElevation: adjustedMin,
        totalDistance: distance
      }
    }

    return {
      maxElevation: max,
      minElevation: min,
      totalDistance: distance
    }
  }, [elevationData])

  // State for tracking hover position
  const [hoverDistance, setHoverDistance] = useState<number | null>(null)

  // Mouse event handlers
  const handleMouseMove = useCallback((e: React.MouseEvent<SVGElement>) => {
    if (!onHoverDistanceChange || totalDistance === 0) return

    const svg = e.currentTarget as SVGSVGElement
    const rect = svg.getBoundingClientRect()
    
    // Get the mouse position relative to the SVG element
    const point = svg.createSVGPoint()
    point.x = e.clientX
    point.y = e.clientY
    
    // Transform to SVG coordinates
    const ctm = svg.getScreenCTM()
    if (!ctm) return // Exit early if SVG is not properly attached to DOM
    let svgPoint = point.matrixTransform(ctm.inverse())
    
    // Adjust for parent scale transform if needed
    if (scaleFactor !== 1) {
      svgPoint.x = svgPoint.x / scaleFactor
      svgPoint.y = svgPoint.y / scaleFactor
    }
    
    // Chart dimensions in SVG coordinates - must match the SVG margins
    const margin = { 
      left: isMobile ? 55 : isTablet ? 45 : 50,
      right: isMobile ? 35 : isTablet ? 25 : 20
    }
    const currentViewBoxWidth = 1000 // Always use desktop viewBox width
    const chartWidth = currentViewBoxWidth - margin.left - margin.right
    
    // Calculate x position relative to chart area
    const x = svgPoint.x - margin.left
    
    // Check if mouse is within chart area
    if (x >= 0 && x <= chartWidth) {
      // Calculate distance based on x position
      const distance = (x / chartWidth) * totalDistance
      setHoverDistance(distance)
      onHoverDistanceChange(distance)
    } else {
      setHoverDistance(null)
      onHoverDistanceChange(null)
    }
  }, [totalDistance, onHoverDistanceChange])

  const handleMouseLeave = useCallback(() => {
    setHoverDistance(null)
    if (onHoverDistanceChange) {
      onHoverDistanceChange(null)
    }
  }, [onHoverDistanceChange])

  // Calculate POI positions along the route
  const poiPositions = useMemo(() => {
    if (pois.length === 0 || !route.coordinates || elevationData.length === 0) return []

    // Filter POIs to only show those with showInElevation enabled
    const poisToShow = pois.filter(poi => poi.showInElevation === true)
    if (poisToShow.length === 0) return []

    const routeCoordinates = route.coordinates
    const proximityThresholdKm = 1 // 1km threshold
    
    return poisToShow.flatMap(poi => {
      if (!poi.coordinates) return []

      // Find ALL route segments within 1km of this POI
      const nearbySegments: Array<{
        distance: number
        elevation: number
        proximityToRoute: number
        index: number
      }> = []

      routeCoordinates.forEach((coord, index) => {
        const [routeLng, routeLat] = coord
        const distance = Math.sqrt(
          Math.pow(routeLat - poi.coordinates.lat, 2) + 
          Math.pow(routeLng - poi.coordinates.lng, 2)
        )

        const proximityMeters = distance * 111000 // Convert degrees to approximate meters

        // If this route point is within 1km of the POI
        if (proximityMeters <= proximityThresholdKm * 1000) {
          const elevationPoint = elevationData[index]
          if (elevationPoint) {
            nearbySegments.push({
              distance: elevationPoint.distance,
              elevation: elevationPoint.elevation,
              proximityToRoute: proximityMeters,
              index
            })
          }
        }
      })

      // If no segments found within 1km, don't show the POI
      if (nearbySegments.length === 0) {
        return []
      }

      // Group nearby segments to avoid cluttering (merge segments within 10km of each other)
      const groupedSegments: Array<typeof nearbySegments[0]> = []
      const sortedSegments = nearbySegments.sort((a, b) => a.distance - b.distance)
      
      for (const segment of sortedSegments) {
        const nearbyGroup = groupedSegments.find(group => 
          Math.abs(group.distance - segment.distance) < 10.0 // Within 10km to prevent duplicate POIs
        )
        
        if (!nearbyGroup) {
          groupedSegments.push(segment)
        } else {
          // Keep the segment with better proximity to the POI
          if (segment.proximityToRoute < nearbyGroup.proximityToRoute) {
            const groupIndex = groupedSegments.indexOf(nearbyGroup)
            groupedSegments[groupIndex] = segment
          }
        }
      }

      const poiType = getPOITypeById(poi.type)
      
      // Return one POI position for each grouped segment
      return groupedSegments.map((segment, segmentIndex) => ({
        poi,
        poiType,
        distance: segment.distance,
        elevation: segment.elevation,
        proximityToRoute: segment.proximityToRoute,
        isMultiple: groupedSegments.length > 1,
        segmentIndex
      }))
    }).filter(Boolean)
  }, [pois, route.coordinates, elevationData])

  // Calculate segment start positions for master route
  const segmentStartPositions = useMemo(() => {
    if (!isMasterRoute || segmentRoutes.length === 0) return []
    
    const positions: Array<{
      distance: number
      segmentNumber: number
      name: string
    }> = []
    
    let cumulativeDistance = 0
    segmentRoutes.forEach((segment, index) => {
      positions.push({
        distance: cumulativeDistance,
        segmentNumber: index + 1,
        name: segment.name
      })
      cumulativeDistance += segment.stats.distance
    })
    
    return positions
  }, [isMasterRoute, segmentRoutes])

  const { pathData, gradientSegments, unpavedSegments } = useMemo(() => {
    if (elevationData.length === 0) return { pathData: '', gradientSegments: [], unpavedSegments: [] }

    // Add responsive margins for axes labels
    const margin = { 
      top: isMobile ? 30 : isTablet ? 25 : 9, 
      right: isMobile ? 35 : isTablet ? 25 : 20, 
      bottom: isMobile ? 30 : isTablet ? 25 : 0, 
      left: isMobile ? 55 : isTablet ? 45 : 50 
    }
    const currentViewBoxWidth = 1000 // Same for all screen sizes now
    const width = currentViewBoxWidth - margin.left - margin.right
    const height = 200 - margin.top - margin.bottom  // Consistent height matching viewBox
    const elevationRange = maxElevation - minElevation || 1

    // Calculate gradient segments every 300m
    const segments = []
    const segmentDistance = 0.3 // 300m in km
    
    for (let distance = 0; distance < totalDistance; distance += segmentDistance) {
      const endDistance = Math.min(distance + segmentDistance, totalDistance)
      
      // Find elevation points for this segment
      // For start: find the last point <= distance (or first point if none)
      let startPoint = elevationData[0]
      for (let i = elevationData.length - 1; i >= 0; i--) {
        if (elevationData[i].distance <= distance) {
          startPoint = elevationData[i]
          break
        }
      }
      
      // For end: find the first point >= endDistance (or last point if none)
      let endPoint = elevationData[elevationData.length - 1]
      for (let i = 0; i < elevationData.length; i++) {
        if (elevationData[i].distance >= endDistance) {
          endPoint = elevationData[i]
          break
        }
      }
      
      // If the points are exactly at our segment boundaries, use them directly
      // Otherwise, interpolate to get the exact elevation at our segment boundaries
      let startElevation = startPoint.elevation
      let endElevation = endPoint.elevation
      
      // Interpolate start elevation if needed
      if (startPoint.distance < distance && elevationData.length > 1) {
        // Find the next point after startPoint
        const startIndex = elevationData.indexOf(startPoint)
        if (startIndex < elevationData.length - 1) {
          const nextPoint = elevationData[startIndex + 1]
          const ratio = (distance - startPoint.distance) / (nextPoint.distance - startPoint.distance)
          startElevation = startPoint.elevation + (nextPoint.elevation - startPoint.elevation) * ratio
        }
      }
      
      // Interpolate end elevation if needed
      if (endPoint.distance > endDistance && elevationData.length > 1) {
        // Find the previous point before endPoint
        const endIndex = elevationData.indexOf(endPoint)
        if (endIndex > 0) {
          const prevPoint = elevationData[endIndex - 1]
          const ratio = (endDistance - prevPoint.distance) / (endPoint.distance - prevPoint.distance)
          endElevation = prevPoint.elevation + (endPoint.elevation - prevPoint.elevation) * ratio
        }
      }
      
      const elevationDiff = endElevation - startElevation
      const distanceDiff = (endDistance - distance) * 1000 // Convert to meters
      const gradient = distanceDiff > 0 ? (elevationDiff / distanceDiff) * 100 : 0
      
      // Use interpolated color for discrete segments
      const color = interpolateColor(gradient)
      
      const startX = margin.left + (distance / totalDistance) * width
      const endX = margin.left + (endDistance / totalDistance) * width
      
      // Get elevation points for this segment
      const segmentPoints = elevationData.filter(p => p.distance >= distance && p.distance <= endDistance)
      if (segmentPoints.length === 0) {
        segmentPoints.push(
          { distance, elevation: startElevation },
          { distance: endDistance, elevation: endElevation }
        )
      }
      
      segments.push({
        startX,
        endX,
        color,
        gradient: gradient.toFixed(1),
        points: segmentPoints
      })
    }

    // Create elevation line points
    const points = elevationData.map((point) => {
      const x = margin.left + (point.distance / totalDistance) * width
      const normalizedElevation = (point.elevation - minElevation) / elevationRange
      const y = margin.top + height - (normalizedElevation * height)
      return `${x},${y}`
    })

    // Ensure path starts and ends at chart bounds
    if (points.length > 0) {
      const firstY = points[0].split(',')[1]
      const lastY = points[points.length - 1].split(',')[1]
      
      points[0] = `${margin.left},${firstY}`
      points[points.length - 1] = `${margin.left + width},${lastY}`
    }

    // Create unpaved segments for overlay that follow the elevation profile
    const unpavedAreas: Array<{ path: string }> = []
    if (surfaces.length > 0) {
      let currentUnpavedPath: string[] = []
      let isInUnpavedSegment = false
      
      elevationData.forEach((point, index) => {
        const x = margin.left + (point.distance / totalDistance) * width
        const normalizedElevation = (point.elevation - minElevation) / elevationRange
        const y = margin.top + height - (normalizedElevation * height)
        const isUnpaved = surfaces[index] === 'unpaved'
        
        if (isUnpaved && !isInUnpavedSegment) {
          // Start new unpaved segment
          currentUnpavedPath = [`M${x},${y}`]
          isInUnpavedSegment = true
        } else if (isUnpaved && isInUnpavedSegment) {
          // Continue unpaved segment
          currentUnpavedPath.push(`L${x},${y}`)
        } else if (!isUnpaved && isInUnpavedSegment) {
          // End unpaved segment - get the last elevation point in the unpaved section
          const lastUnpavedIndex = index - 1
          if (lastUnpavedIndex >= 0) {
            const lastPoint = elevationData[lastUnpavedIndex]
            const lastX = margin.left + (lastPoint.distance / totalDistance) * width
            const lastNormalizedElevation = (lastPoint.elevation - minElevation) / elevationRange
            const lastY = margin.top + height - (lastNormalizedElevation * height)
            
            // Make sure we end at the last unpaved point
            currentUnpavedPath.push(`L${lastX},${lastY}`)
            
            // Close the area like gradient fill: down to bottom, across to start, close  
            const firstPoint = currentUnpavedPath[0].replace('M', '').split(',')
            const startX = parseFloat(firstPoint[0])
            // Use the SAME bottom calculation as the SVG inner scope
            const svgMargin = { 
              top: isMobile ? 30 : isTablet ? 25 : 9, 
              right: isMobile ? 35 : isTablet ? 25 : 20, 
              bottom: isMobile ? 30 : isTablet ? 25 : 0, 
              left: isMobile ? 55 : isTablet ? 45 : 50 
            }
            const svgChartHeight = 200 - svgMargin.top - svgMargin.bottom  // Consistent height matching viewBox
            const chartBottom = svgMargin.top + svgChartHeight
            currentUnpavedPath.push(`L${lastX},${chartBottom}`)
            currentUnpavedPath.push(`L${startX},${chartBottom}`)
            currentUnpavedPath.push('Z')
          }
          
          unpavedAreas.push({ path: currentUnpavedPath.join('') })
          currentUnpavedPath = []
          isInUnpavedSegment = false
        }
      })
      
      // Close final segment if it ends unpaved
      if (isInUnpavedSegment && currentUnpavedPath.length > 0) {
        const firstPoint = currentUnpavedPath[0].replace('M', '').split(',')
        const startX = parseFloat(firstPoint[0])
        const lastElevationPoint = elevationData[elevationData.length - 1]
        const endX = margin.left + (lastElevationPoint.distance / totalDistance) * width
        const endY = margin.top + height - ((lastElevationPoint.elevation - minElevation) / elevationRange * height)
        currentUnpavedPath.push(`L${endX},${endY}`)
        const svgMargin = { 
          top: isMobile ? 30 : isTablet ? 25 : 9, 
          right: isMobile ? 35 : isTablet ? 25 : 20, 
          bottom: isMobile ? 30 : isTablet ? 25 : 0, 
          left: isMobile ? 55 : isTablet ? 45 : 50 
        }
        const svgChartHeight = 200 - svgMargin.top - svgMargin.bottom  // Consistent height matching viewBox
        const chartBottom = svgMargin.top + svgChartHeight
        currentUnpavedPath.push(`L${endX},${chartBottom}`)
        currentUnpavedPath.push(`L${startX},${chartBottom}`)
        currentUnpavedPath.push('Z')
        unpavedAreas.push({ path: currentUnpavedPath.join('') })
      }
    }

    return {
      pathData: `M${points.join('L')}`,
      gradientSegments: segments,
      unpavedSegments: unpavedAreas
    }
  }, [elevationData, maxElevation, minElevation, totalDistance, surfaces, isMobile, isTablet])

  if (elevationData.length === 0) {
    return (
      <div className={`text-center text-gray-400 py-2 px-1 ${className}`}>
        <p className="text-xs">No elevation data available for this route</p>
      </div>
    )
  }

  
  // Consistent viewBox height across all screen sizes
  const getViewBox = () => {
    // Use the same height for all screen sizes so the chart doesn't change height
    return "0 -30 1000 250"  // Consistent height for all
  }
  
  const viewBoxWidth = isMobile ? 1000 : 1000
  
  return (
    <div className={`h-full flex flex-col ${isMobile ? 'px-6' : isTablet ? 'px-4' : 'p-1'} bg-transparent ${isMobile ? '' : 'rounded-lg'} ${className}`} style={{ position: 'relative', zIndex: 50 }}>
      {/* Scroll indicator for mobile */}
      {isMobile && (
        <div className="text-xs text-gray-400 text-center mb-2">
          <span className="inline-flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            Swipe to scroll
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
          </span>
        </div>
      )}
      <div 
        className={`flex-1 min-h-0 relative ${isMobile ? 'overflow-x-auto overflow-y-hidden -mx-6' : 'overflow-hidden'}`} 
        style={{ 
          zIndex: 50, 
          height: '100%',
          ...(isMobile ? { 
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            scrollbarColor: '#4b5563 transparent'
          } : {})
        }}
      >
        <div className={isMobile ? 'px-6' : ''} style={{ 
          minWidth: isMobile ? '150%' : 'auto',
          height: '100%',
          maxHeight: '200px',  // Same max height for all
          ...(isMobile ? {
            transform: 'scale(1.25)',
            transformOrigin: 'left center'
          } : {})
        }}>
          <svg 
            width="100%" 
            height="100%"
            viewBox={getViewBox()} 
            preserveAspectRatio="xMidYMid meet"
            className="overflow-visible"
            style={{ 
              display: 'block'
            }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleMouseMove}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseLeave}
        >
          {/* Define margins */}
          {(() => {
            const margin = { 
              top: isMobile ? 30 : isTablet ? 25 : 9, 
              right: isMobile ? 35 : isTablet ? 25 : 20, 
              bottom: isMobile ? 30 : isTablet ? 25 : 0, 
              left: isMobile ? 55 : isTablet ? 45 : 50 
            }
            const chartWidth = viewBoxWidth - margin.left - margin.right
            const chartHeight = 200 - margin.top - margin.bottom  // Consistent height matching viewBox
            
            return (
              <>
                {/* Grid lines */}
                <defs>
                  <pattern id="grid" width="100" height="16" patternUnits="userSpaceOnUse">
                    <path d="M 100 0 L 0 0 0 16" fill="none" stroke="#4b5563" strokeWidth="0.5" opacity="0.3"/>
                  </pattern>
                  <pattern id="diagonalHatch" width="8" height="8" patternUnits="userSpaceOnUse">
                    <path d="M0,8 L8,0" stroke="#000000" strokeWidth="1" opacity="0.4"/>
                  </pattern>
                  <linearGradient id="elevationStepGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    {gradientSegments.map((segment, index) => {
                      const offset = ((segment.startX - margin.left) / chartWidth) * 100;
                      const nextSegment = gradientSegments[index + 1];
                      const nextOffset = nextSegment ? ((nextSegment.startX - margin.left) / chartWidth) * 100 : 100;
                      
                      return [
                        <stop 
                          key={`${index}-start`} 
                          offset={`${offset}%`} 
                          stopColor={segment.color} 
                          stopOpacity="0.8"
                        />,
                        <stop 
                          key={`${index}-end`} 
                          offset={`${nextOffset}%`} 
                          stopColor={segment.color} 
                          stopOpacity="0.8"
                        />
                      ];
                    }).flat()}
                  </linearGradient>
                </defs>
                
                {/* Grid lines as individual lines for transparency */}
                {(() => {
                  const gridLines = []
                  // Horizontal grid lines (4 lines)
                  for (let i = 0; i <= 4; i++) {
                    const y = margin.top + (i * chartHeight / 4)
                    gridLines.push(
                      <line 
                        key={`h-grid-${i}`}
                        x1={margin.left} 
                        y1={y} 
                        x2={margin.left + chartWidth} 
                        y2={y} 
                        stroke="#4b5563" 
                        strokeWidth="0.5" 
                        opacity="0.3"
                      />
                    )
                  }
                  return gridLines
                })()}
                
                {/* Y-axis */}
                <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + chartHeight} stroke="#9ca3af" strokeWidth="1"/>
                
                {/* X-axis */}
                <line x1={margin.left} y1={margin.top + chartHeight} x2={margin.left + chartWidth} y2={margin.top + chartHeight} stroke="#9ca3af" strokeWidth="1"/>
                
                {/* Y-axis labels (elevation) */}
                {(() => {
                  const elevationRange = maxElevation - minElevation || 1
                  const steps = 4
                  const stepSize = elevationRange / steps
                  const labels = []
                  
                  for (let i = 0; i <= steps; i++) {
                    const elevation = minElevation + (stepSize * i)
                    const y = margin.top + chartHeight - (i * (chartHeight / steps))
                    labels.push(
                      <g key={i}>
                        <line x1={margin.left - 5} y1={y} x2={margin.left + 5} y2={y} stroke="#9ca3af" strokeWidth="1"/>
                        <text x={margin.left - 8} y={y + 3} fontSize={isMobile || isTablet ? "12" : "10"} fill="#f3f4f6" textAnchor="end" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>
                          {Math.round(elevation)}m
                        </text>
                      </g>
                    )
                  }
                  return labels
                })()}
                
                {/* X-axis labels (distance) */}
                {(() => {
                  // Get POI distances that are being displayed (only for non-master routes)
                  const poiDistances = !isMasterRoute ? poiPositions
                    .filter(poiPos => poiPos && poiPos.proximityToRoute <= 1000)
                    .map(poiPos => Math.round(poiPos!.distance)) : []
                  
                  // Create initial set of distances to label
                  const candidateDistances = new Set()
                  
                  // Add regular interval distances (every 5-10km depending on total distance)
                  const steps = Math.min(10, Math.ceil(totalDistance / 5))
                  const stepDistance = totalDistance / steps
                  for (let i = 0; i <= steps; i++) {
                    const distance = Math.round(i * stepDistance)
                    candidateDistances.add(distance)
                  }
                  
                  // For master route, add segment positions
                  if (isMasterRoute) {
                    segmentStartPositions.forEach(segPos => {
                      candidateDistances.add(Math.round(segPos.distance))
                    })
                  } else {
                    // For regular routes, add POI distances
                    poiDistances.forEach(distance => {
                      candidateDistances.add(distance)
                    })
                  }
                  
                  // Convert to array and sort
                  const sortedCandidates = Array.from(candidateDistances).sort((a: unknown, b: unknown) => (a as number) - (b as number))
                  
                  // Filter to prevent overlapping labels (minimum ~2km spacing or 25px)
                  const minSpacingKm = Math.max(2, totalDistance * 25 / chartWidth)
                  const finalDistances: number[] = []
                  
                  // Always include start
                  finalDistances.push(0)
                  
                  // For master route, add segment positions first (highest priority)
                  if (isMasterRoute) {
                    segmentStartPositions.forEach(segPos => {
                      const distance = Math.round(segPos.distance)
                      if (distance > 0 && distance < Math.round(totalDistance)) {
                        finalDistances.push(distance)
                      }
                    })
                  } else {
                    // For regular routes, add ALL POI distances first (highest priority - no spacing restrictions)
                    poiDistances.forEach(distance => {
                      if (distance > 0 && distance < Math.round(totalDistance)) {
                        finalDistances.push(distance)
                      }
                    })
                  }
                  
                  // Add regular intervals only if they don't conflict with POIs
                  sortedCandidates.forEach((distance: unknown) => {
                    if ((distance as number) > 0 && (distance as number) < Math.round(totalDistance) && 
                        !finalDistances.includes(distance as number)) {
                      // Check if this regular interval is far enough from existing labels
                      const tooClose = finalDistances.some(existingDistance => 
                        Math.abs(existingDistance - (distance as number)) < minSpacingKm
                      )
                      if (!tooClose) {
                        finalDistances.push(distance as number)
                      }
                    }
                  })
                  
                  // Always include end
                  const endDistance = Math.round(totalDistance)
                  if (!finalDistances.includes(endDistance)) {
                    finalDistances.push(endDistance)
                  }
                  
                  // Sort final distances
                  finalDistances.sort((a, b) => a - b)
                  
                  const labels: JSX.Element[] = []
                  finalDistances.forEach((distance) => {
                    const x = margin.left + (distance / totalDistance) * chartWidth
                    labels.push(
                      <g key={`distance-${distance}`}>
                        <line x1={x} y1={margin.top + chartHeight - 5} x2={x} y2={margin.top + chartHeight + 5} stroke="#9ca3af" strokeWidth="1"/>
                        <text x={x} y={margin.top + chartHeight + 18} fontSize={isMobile || isTablet ? "12" : "10"} fill="#f3f4f6" textAnchor="middle" style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}>
                          {distance}
                        </text>
                      </g>
                    )
                  })
                  return labels
                })()}
                
                {/* Step gradient fill area */}
                <path
                  d={`${pathData}L${margin.left + chartWidth},${margin.top + chartHeight}L${margin.left},${margin.top + chartHeight}Z`}
                  fill="url(#elevationStepGradient)"
                />
                
                {/* Elevation path */}
                <path
                  d={pathData}
                  fill="none"
                  stroke="#000000"
                  strokeWidth={isMobile ? "0.75" : "0.5"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {/* Unpaved surface overlay */}
                {unpavedSegments.map((segment, index) => (
                  <path
                    key={index}
                    d={segment.path}
                    fill="url(#diagonalHatch)"
                  />
                ))}

                {/* POI markers OR Segment markers */}
                {isMasterRoute ? (
                  // Show segment start markers for master route
                  segmentStartPositions.map((segmentPos, index) => {
                    const x = margin.left + (segmentPos.distance / totalDistance) * chartWidth
                    const y = margin.top - 14 // Fixed position above the chart
                    
                    return (
                      <g key={`segment-${segmentPos.segmentNumber}`}>
                        {/* Vertical line connecting segment marker to profile */}
                        <line
                          x1={x}
                          y1={y + 12}
                          x2={x}
                          y2={margin.top + chartHeight}
                          stroke="#6b7280"
                          strokeWidth="1"
                          strokeDasharray="2,2"
                          opacity="0.6"
                        />
                        
                        {/* Segment marker circle */}
                        <circle
                          cx={x}
                          cy={y}
                          r="8"
                          fill="#10b981"
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                        >
                          <title>Start of {segmentPos.name}</title>
                        </circle>
                        
                        {/* Segment number */}
                        <text
                          x={x}
                          y={y + 4}
                          fontSize="11"
                          fill="white"
                          textAnchor="middle"
                          fontWeight="bold"
                          className="pointer-events-none"
                        >
                          {segmentPos.segmentNumber}
                        </text>
                      </g>
                    )
                  })
                ) : (
                  // Show POI markers for regular routes
                  poiPositions.map((poiPos, index) => {
                    if (!poiPos || !poiPos.poiType) return null
                    
                    const x = margin.left + (poiPos.distance / totalDistance) * chartWidth
                    // Position POI above the chart area
                    const y = margin.top - 14 // Fixed position above the chart
                    
                    // Only show POIs that are reasonably close to the route (within 1km)
                    if (poiPos.proximityToRoute > 1000) return null
                    
                    const IconComponent = poiPos.poiType.icon
                    
                    // Get color from POI category instead of stored color
                    const poiCategory = getPOICategoryById(poiPos.poiType.category)
                    const poiColor = poiCategory?.color || '#6b7280' // Default gray if category not found
                    
                    // Create unique key for multiple instances of the same POI
                    const uniqueKey = `poi-${poiPos.poi.id}-${poiPos.segmentIndex || 0}`
                    
                    return (
                      <g key={uniqueKey}>
                        {/* Vertical line connecting POI to profile */}
                        <line
                          x1={x}
                          y1={y + 12}
                          x2={x}
                          y2={margin.top + chartHeight}
                          stroke="#6b7280"
                          strokeWidth="1"
                          strokeDasharray="2,2"
                          opacity="0.6"
                        />
                        
                        {/* POI marker circle */}
                        <circle
                          cx={x}
                          cy={y}
                          r="8"
                          fill={poiColor}
                          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                        >
                          {/* Add tooltip for hover */}
                          <title>{poiPos.poi.name}</title>
                        </circle>
                        
                        {/* POI icon */}
                        <foreignObject x={x - 6} y={y - 6} width="12" height="12">
                          <div className="flex items-center justify-center w-full h-full">
                            <IconComponent className="w-3 h-3 text-white" strokeWidth={2} />
                          </div>
                        </foreignObject>
                        
                        {/* POI label - only show if hideElevationLabel is not true */}
                        {!poiPos.poi.hideElevationLabel && (
                          <text
                            x={x}
                            y={y - 12}
                            fontSize="9"
                            fill="#d1d5db"
                            textAnchor="middle"
                            className="pointer-events-none"
                          >
                            {poiPos.poi.name}
                          </text>
                        )}
                      </g>
                    )
                  })
                )}

                {/* Climb flags - only show for individual routes, not master route */}
                {climbs && climbs.length > 0 && !isMasterRoute && (
                  <>
                    {climbs.map((climb, index) => {
                      // Calculate positions
                      const startX = margin.left + (climb.startDistance / totalDistance) * chartWidth
                      const endX = margin.left + (climb.endDistance / totalDistance) * chartWidth
                      
                      // Find elevation at climb start
                      const startElevation = elevationData.find(p => 
                        Math.abs(p.distance - climb.startDistance) < 0.01
                      )?.elevation || minElevation
                      
                      // Calculate Y position based on elevation
                      const elevationRange = maxElevation - minElevation || 1
                      const normalizedElevation = (startElevation - minElevation) / elevationRange
                      const startY = margin.top + chartHeight - (normalizedElevation * chartHeight)
                      
                      // Unique ID for this climb
                      const climbId = `climb-${index}`
                      
                      return (
                        <g 
                          key={climbId}
                          className={`climb-group climb-group-${index}`}
                          onMouseEnter={() => handleClimbMouseEnter(index)}
                          onMouseLeave={() => handleClimbMouseLeave()}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* Large invisible hover target */}
                          <rect
                            x={startX - 15}
                            y={-30}
                            width={endX - startX + 30}
                            height={isMobile ? 200 : 180}
                            fill="transparent"
                            opacity="0"
                          />
                          
                          {/* Background highlight for climb section - only visible on hover */}
                          <rect
                            x={startX}
                            y={margin.top}
                            width={(() => {
                              // Find the actual peak within this climb to stop highlighting there
                              const climbData = elevationData.filter(point => 
                                point.distance >= climb.startDistance && 
                                point.distance <= climb.endDistance
                              )
                              
                              if (climbData.length === 0) return endX - startX
                              
                              // Find the highest point in this climb
                              let peakDistance = climb.startDistance
                              let peakElevation = climbData[0].elevation
                              climbData.forEach(point => {
                                if (point.elevation > peakElevation) {
                                  peakElevation = point.elevation
                                  peakDistance = point.distance
                                }
                              })
                              
                              // Calculate X position of the peak
                              const peakX = margin.left + (peakDistance / totalDistance) * chartWidth
                              
                              // Use peak position instead of original end position
                              return Math.max(peakX - startX, 20) // Minimum 20px width
                            })()}
                            height={chartHeight}
                            fill={climb.color}
                            opacity={hoveredClimbIndex === index ? 0.12 : 0}
                            style={{ transition: 'opacity 0.2s', pointerEvents: 'none' }}
                          />
                          
                          {/* Start line - only visible on hover */}
                          <line
                            x1={startX}
                            y1={margin.top}
                            x2={startX}
                            y2={margin.top + chartHeight}
                            stroke="#d1d5db"
                            strokeWidth="1"
                            strokeDasharray="4,2"
                            opacity={hoveredClimbIndex === index ? 0.6 : 0}
                            style={{ transition: 'opacity 0.2s', pointerEvents: 'none' }}
                          />
                          
                          {/* End line - only visible on hover */}
                          <line
                            x1={(() => {
                              // Calculate peak position for end line
                              const climbData = elevationData.filter(point => 
                                point.distance >= climb.startDistance && 
                                point.distance <= climb.endDistance
                              )
                              
                              if (climbData.length === 0) return endX
                              
                              // Find the highest point in this climb
                              let peakDistance = climb.startDistance
                              let peakElevation = climbData[0].elevation
                              climbData.forEach(point => {
                                if (point.elevation > peakElevation) {
                                  peakElevation = point.elevation
                                  peakDistance = point.distance
                                }
                              })
                              
                              // Calculate X position of the peak
                              return margin.left + (peakDistance / totalDistance) * chartWidth
                            })()}
                            y1={margin.top}
                            x2={(() => {
                              // Same calculation for x2
                              const climbData = elevationData.filter(point => 
                                point.distance >= climb.startDistance && 
                                point.distance <= climb.endDistance
                              )
                              
                              if (climbData.length === 0) return endX
                              
                              let peakDistance = climb.startDistance
                              let peakElevation = climbData[0].elevation
                              climbData.forEach(point => {
                                if (point.elevation > peakElevation) {
                                  peakElevation = point.elevation
                                  peakDistance = point.distance
                                }
                              })
                              
                              return margin.left + (peakDistance / totalDistance) * chartWidth
                            })()}
                            y2={margin.top + chartHeight}
                            stroke="#d1d5db"
                            strokeWidth="1"
                            strokeDasharray="4,2"
                            opacity={hoveredClimbIndex === index ? 0.6 : 0}
                            style={{ transition: 'opacity 0.2s', pointerEvents: 'none' }}
                          />
                          
                          {/* Stats text background - only visible on hover */}
                          <rect
                            x={(startX + endX) / 2 - 65}
                            y={margin.top - 17}
                            width="130"
                            height="15"
                            rx="2"
                            fill="#1f2937"
                            opacity={hoveredClimbIndex === index ? 0.9 : 0}
                            style={{ transition: 'opacity 0.2s', pointerEvents: 'none' }}
                          />
                          
                          {/* Climb stats text - only visible on hover */}
                          <text
                            x={(startX + endX) / 2}
                            y={margin.top - 6}
                            fontSize="9"
                            fill={climb.color}
                            textAnchor="middle"
                            fontWeight="400"
                            opacity={hoveredClimbIndex === index ? 1 : 0}
                            style={{ transition: 'opacity 0.2s', pointerEvents: 'none' }}
                          >
                            ({climb.category}) {(climb.length / 1000).toFixed(1)}km @ {climb.averageGrade.toFixed(1)}%, {Math.round(climb.elevationGain)}m
                          </text>
                          
                          
                          {/* Flag pole */}
                          <line
                            x1={startX}
                            y1={startY - 25 + 10}
                            x2={startX}
                            y2={startY}
                            stroke="#374151"
                            strokeWidth="1"
                            opacity="0.8"
                            style={{ pointerEvents: 'none' }}
                          />
                          
                          {/* Pennant flag shape - small */}
                          <path
                            d={`M ${startX} ${startY - 25} L ${startX + 14} ${startY - 25 + 5} L ${startX} ${startY - 25 + 10} Z`}
                            fill={climb.color}
                            stroke="#374151"
                            strokeWidth="0.5"
                            style={{ 
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                              pointerEvents: 'none'
                            }}
                          >
                            <title>
                              {climb.category} climb: {climb.length.toFixed(0)}m, {climb.elevationGain.toFixed(0)}m gain, {climb.averageGrade.toFixed(1)}% grade
                            </title>
                          </path>
                        </g>
                      )
                    })}
                  </>
                )}

                {/* Hover indicator */}
                {hoverDistance !== null && (() => {
                  const x = margin.left + (hoverDistance / totalDistance) * chartWidth
                  
                  // Find elevation at hover position by interpolating between points
                  let elevation = 0
                  
                  // Find the two points that bracket the hover distance
                  let point1 = elevationData[0]
                  let point2 = elevationData[0]
                  
                  for (let i = 0; i < elevationData.length - 1; i++) {
                    if (elevationData[i].distance <= hoverDistance && 
                        elevationData[i + 1].distance >= hoverDistance) {
                      point1 = elevationData[i]
                      point2 = elevationData[i + 1]
                      break
                    }
                  }
                  
                  // If we're past the end, use the last two points
                  if (hoverDistance >= elevationData[elevationData.length - 1].distance) {
                    point1 = elevationData[elevationData.length - 2]
                    point2 = elevationData[elevationData.length - 1]
                  }
                  
                  // Interpolate elevation between the two points
                  if (point1 && point2 && point2.distance > point1.distance) {
                    const segmentDist = point2.distance - point1.distance
                    const distInSegment = hoverDistance - point1.distance
                    const ratio = distInSegment / segmentDist
                    elevation = point1.elevation + (point2.elevation - point1.elevation) * ratio
                  } else {
                    elevation = point1.elevation
                  }
                  
                  const y = margin.top + chartHeight - 
                           ((elevation - minElevation) / (maxElevation - minElevation || 1) * chartHeight)
                  
                  return (
                    <g>
                      {/* Vertical line */}
                      <line
                        x1={x}
                        y1={margin.top}
                        x2={x}
                        y2={margin.top + chartHeight}
                        stroke="#9ca3af"
                        strokeWidth="1"
                        strokeDasharray="2,2"
                        opacity="0.8"
                      />
                      
                      {/* Dot at elevation */}
                      <defs>
                        <radialGradient id="elevationDotGradient" cx="30%" cy="30%">
                          <stop offset="0%" stopColor="#60a5fa" />
                          <stop offset="40%" stopColor="#3b82f6" />
                          <stop offset="100%" stopColor="#2563eb" />
                        </radialGradient>
                      </defs>
                      <circle
                        cx={x}
                        cy={y}
                        r="4"
                        fill="url(#elevationDotGradient)"
                        stroke="#ffffff"
                        strokeWidth="1"
                      />
                      
                      {/* Distance label */}
                      <rect
                        x={x - 30}
                        y={margin.top + chartHeight + 5}
                        width="60"
                        height="16"
                        fill="#1f2937"
                        stroke="#4b5563"
                        strokeWidth="1"
                        rx="2"
                      />
                      <text
                        x={x}
                        y={margin.top + chartHeight + 17}
                        fontSize="10"
                        fill="#d1d5db"
                        textAnchor="middle"
                        fontWeight="normal"
                      >
                        {hoverDistance.toFixed(1)}km
                      </text>
                    </g>
                  )
                })()}

                {/* Flyby tracer */}
                {flybyProgress !== undefined && flybyProgress > 0 && (() => {
                  const flybyDistance = (flybyProgress / 100) * totalDistance
                  const x = margin.left + (flybyDistance / totalDistance) * chartWidth
                  
                  // Find elevation at flyby position by interpolating between points
                  let elevation = 0
                  let point1 = elevationData[0]
                  let point2 = elevationData[0]
                  
                  for (let i = 0; i < elevationData.length - 1; i++) {
                    if (elevationData[i].distance <= flybyDistance && 
                        elevationData[i + 1].distance >= flybyDistance) {
                      point1 = elevationData[i]
                      point2 = elevationData[i + 1]
                      break
                    }
                  }
                  
                  // Interpolate elevation between the two points
                  if (point1 && point2 && point2.distance > point1.distance) {
                    const segmentDist = point2.distance - point1.distance
                    const distInSegment = flybyDistance - point1.distance
                    const ratio = distInSegment / segmentDist
                    elevation = point1.elevation + (point2.elevation - point1.elevation) * ratio
                  } else {
                    elevation = point1.elevation
                  }
                  
                  const y = margin.top + chartHeight - 
                           ((elevation - minElevation) / (maxElevation - minElevation || 1) * chartHeight)
                  
                  return (
                    <g>
                      {/* Flyby marker with pulsing animation */}
                      <defs>
                        <radialGradient id="flybyTracerGradient" cx="30%" cy="30%">
                          <stop offset="0%" stopColor="#10b981" />
                          <stop offset="40%" stopColor="#059669" />
                          <stop offset="100%" stopColor="#047857" />
                        </radialGradient>
                      </defs>
                      
                      {/* Outer pulsing ring */}
                      <circle
                        cx={x}
                        cy={y}
                        r="8"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        opacity="0.6"
                      >
                        <animate
                          attributeName="r"
                          values="6;12;6"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.8;0.2;0.8"
                          dur="2s"
                          repeatCount="indefinite"
                        />
                      </circle>
                      
                      {/* Inner dot */}
                      <circle
                        cx={x}
                        cy={y}
                        r="5"
                        fill="url(#flybyTracerGradient)"
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                      
                    </g>
                  )
                })()}
              </>
            )
          })()}
        </svg>
        </div>
      </div>
    </div>
  )
})